import type { FileRoutesById } from '@/app/route-tree.gen'
import {
  hasPermission,
  type CurrentUser,
  type Permission,
} from '@/types/auth/current-user'

export type PageId = Exclude<keyof FileRoutesById, '__root__'>
export type Shell = 'public' | 'bare' | 'app'
export type Page =
  | { readonly shell: 'public' }
  | {
      readonly permission: Permission | null
      readonly shell: Exclude<Shell, 'public'>
      readonly navLabel: string
    }

export const PAGES = {
  '/chat': { permission: null, shell: 'bare', navLabel: 'Chat' },
  '/': {
    permission: 'SCENARIO_BUILD',
    shell: 'app',
    navLabel: 'Agent Orchestration',
  },
  '/audit-trail': {
    permission: null,
    shell: 'app',
    navLabel: 'Audit Trail',
  },
  '/configuration': {
    permission: 'CONFIGURATION_MANAGE',
    shell: 'app',
    navLabel: 'Configuration',
  },
  '/users': {
    permission: 'USER_MANAGE',
    shell: 'app',
    navLabel: 'Users',
  },
  '/profile': {
    permission: null,
    shell: 'app',
    navLabel: 'Profile',
  },
  '/login': { shell: 'public' },
  '/forgot-password': { shell: 'public' },
  '/set-password': { shell: 'public' },
  '/reset-password': { shell: 'public' },
  '/verify-email': { shell: 'public' },
} as const satisfies Record<PageId, Page>

export interface NavItem {
  label: string
  to: PageId
}

const NOT_FOUND: Page = { shell: 'app', permission: null, navLabel: 'Not Found' }

export function leafRouteId(matches: readonly { routeId: string }[]): string {
  return matches.at(-1)?.routeId ?? ''
}

export function pageFor(routeId: string): Page {
  return Object.hasOwn(PAGES, routeId) ? PAGES[routeId as PageId] : NOT_FOUND
}

export function canOpen(user: CurrentUser, routeId: string): boolean {
  const page = pageFor(routeId)
  return (
    page.shell === 'public' ||
    page.permission === null ||
    hasPermission(user, page.permission)
  )
}

export function visibleNav(user: CurrentUser | null | undefined): readonly NavItem[] {
  if (!user) return []

  return (Object.keys(PAGES) as PageId[]).flatMap((to) => {
    const page = PAGES[to]
    return page.shell !== 'public' && canOpen(user, to)
      ? [{ label: page.navLabel, to }]
      : []
  })
}

export function landingPath(user: CurrentUser): PageId {
  return canOpen(user, '/') ? '/' : (visibleNav(user)[0]?.to ?? '/profile')
}

export function firstAppPage(user: CurrentUser): NavItem {
  return (
    visibleNav(user).find((item) => shellFor(item.to) === 'app') ?? {
      label: PAGES['/profile'].navLabel,
      to: '/profile',
    }
  )
}

export function shellFor(routeId: string): Shell {
  return pageFor(routeId).shell
}

export function accessFor(
  user: CurrentUser | null,
  routeId: string,
): { kind: 'open' } | { kind: 'login' } | { kind: 'elsewhere'; to: PageId } {
  if (pageFor(routeId).shell === 'public') return { kind: 'open' }
  if (user === null) return { kind: 'login' }
  return canOpen(user, routeId)
    ? { kind: 'open' }
    : { kind: 'elsewhere', to: landingPath(user) }
}
